import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import '@/App.css';
import Dashboard from './pages/Dashboard';
import Carriers from './pages/Carriers';
import Shippers from './pages/Shippers';
import Loads from './pages/Loads';
import Agents from './pages/Agents';
import SuperAdmin from './pages/SuperAdmin';
import Pricing from './pages/Pricing';
import DriverPortal from './pages/DriverPortal';
import Auth from './pages/Auth';
import Training from './pages/Training';
import DocumentScanner from './pages/DocumentScanner';
import Messages from './pages/Messages';
import Invoices from './pages/Invoices';
import DispatcherFolders from './pages/DispatcherFolders';
import CreditCheck from './pages/CreditCheck';
import DispatcherAnalytics from './pages/DispatcherAnalytics';
import Drivers from './pages/Drivers';
import SignUp from './pages/SignUp';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentManagement from './pages/PaymentManagement';
import Settlements from './pages/Settlements';
import Compliance from './pages/Compliance';
import MyEarnings from './pages/MyEarnings';
import RevenueBoard from './pages/RevenueBoard';
import AIDispatch from './pages/AIDispatch';
import AIRoundtrip from './pages/AIRoundtrip';
import Profile from './pages/Profile';
import FleetRates from './pages/FleetRates';
import LoadApprovals from './pages/LoadApprovals';
import Layout from './components/Layout';
import { Toaster } from 'sonner';

// Use current domain for API calls - works on preview and custom domain
const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      const masterCode = localStorage.getItem('master_code');
      const userName = localStorage.getItem('user_name');
      const pinCode = localStorage.getItem('pin_code');
      
      api.post('/auth/session', {
        session_id: sessionId,
        master_code: masterCode || '',
        user_name: userName || '',
        pin_code: pinCode || ''
      }).then(res => {
        setUser(res.data.user);
        window.location.hash = '';
        window.history.replaceState(null, null, window.location.pathname);
      }).catch(err => {
        console.error('Auth error:', err);
      }).finally(() => {
        setLoading(false);
        localStorage.removeItem('master_code');
        localStorage.removeItem('user_name');
        localStorage.removeItem('pin_code');
      });
    } else {
      api.get('/auth/me').then(res => {
        setUser(res.data);
      }).catch(() => {
        setUser(null);
      }).finally(() => {
        setLoading(false);
      });
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/auth" element={!user ? <Auth setUser={setUser} /> : <Navigate to="/" />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/driver" element={<DriverPortal />} />
        <Route path="/" element={user ? <Layout user={user} setUser={setUser} /> : <Navigate to="/auth" />}>
          <Route index element={<Dashboard user={user} />} />
          <Route path="carriers" element={<Carriers user={user} />} />
          <Route path="shippers" element={<Shippers user={user} />} />
          <Route path="loads" element={<Loads user={user} />} />
          <Route path="drivers" element={<Drivers user={user} />} />
          <Route path="agents" element={<Agents user={user} />} />
          <Route path="training" element={<Training user={user} />} />
          <Route path="documents" element={<DocumentScanner user={user} />} />
          <Route path="messages" element={<Messages user={user} />} />
          <Route path="invoices" element={<Invoices user={user} />} />
          <Route path="folders" element={<DispatcherFolders user={user} />} />
          <Route path="credit-check" element={<CreditCheck user={user} />} />
          <Route path="analytics" element={<DispatcherAnalytics user={user} />} />
          <Route path="super-admin" element={<SuperAdmin user={user} />} />
          <Route path="payments" element={<PaymentManagement user={user} />} />
          <Route path="settlements" element={<Settlements user={user} />} />
          <Route path="compliance" element={<Compliance user={user} />} />
          <Route path="my-earnings" element={<MyEarnings user={user} />} />
          <Route path="revenue" element={<RevenueBoard user={user} />} />
          <Route path="ai-dispatch" element={<AIDispatch user={user} />} />
          <Route path="ai-roundtrip" element={<AIRoundtrip user={user} />} />
          <Route path="profile" element={<Profile user={user} />} />
          <Route path="fleet-rates" element={<FleetRates user={user} />} />
          <Route path="load-approvals" element={<LoadApprovals user={user} />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
